import create from "zustand";

/**
 * Maximum number of colors a user can save to the Brand Kit. Read
 * by the UI to disable the section-header "+" + add a tooltip in
 * BrandKit.tsx, and to hide the "add to brand kit" button + show a
 * "(max reached)" label in CustomColorPicker.tsx. Lives in the
 * store module so both files can import it without creating a
 * circular dependency between BrandKit ↔ CustomColorPicker.
 */
export const MAX_BRAND_COLORS = 9;

import {
  getBrandKit,
  getBrandKitAssets,
  saveBrandKitColor,
  updateBrandKitColor as updateBrandKitColorMutation,
  removeBrandKitColor as removeBrandKitColorMutation,
  removeBrandKitAsset as removeBrandKitAssetMutation,
  reorderBrandKitColors,
  reorderBrandKitFontPresets,
  saveBrandKitFontPreset as saveBrandKitFontPresetMutation,
  updateBrandKitFontPreset as updateBrandKitFontPresetMutation,
  removeBrandKitFontPreset as removeBrandKitFontPresetMutation,
  BrandKitAssetType,
  BrandKitSection,
  BrandKitColor,
  BrandKitFontPreset,
  SaveBrandKitColorInput,
  UpdateBrandKitColorInput,
  SaveBrandKitFontPresetInput,
  UpdateBrandKitFontPresetInput,
} from "graphQL/mutations/brandKit";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toJSON = (res: any): Promise<any> => res.json();

/**
 * Sync the first font preset to localStorage so new text shapes
 * pick it up as their default font — even before TextParameters mounts.
 */
const syncFirstPresetToLocalStorage = (presets: BrandKitFontPreset[]) => {
  try {
    if (presets.length > 0) {
      const p = presets[0];
      localStorage.setItem("lastSelectedFont", p.fontFamily);
      localStorage.setItem(
        "brandDefaultFontPreset",
        JSON.stringify({
          fontFamily: p.fontFamily,
          fontVariant: p.fontVariant,
          fontSize: p.fontSize,
          lineHeight: p.lineHeight,
          letterSpacing: p.letterSpacing,
        })
      );
    } else {
      localStorage.removeItem("brandDefaultFontPreset");
      localStorage.removeItem("lastSelectedFont");
    }
  } catch {
    // ignore localStorage errors (SSR, quota, etc.)
  }
};

const SECTION_KEY: Record<BrandKitAssetType, "logos" | "images" | "fonts" | "icons"> = {
  logo: "logos",
  image: "images",
  font: "fonts",
  icon: "icons",
};

const emptySection: BrandKitSection = { items: [], totalCount: 0, hasMore: false };

// ─── Store ────────────────────────────────────────────────────────────────────

interface BrandKitStore {
  logos: BrandKitSection;
  images: BrandKitSection;
  fonts: BrandKitSection;
  icons: BrandKitSection;
  colors: BrandKitColor[];
  fontPresets: BrandKitFontPreset[];
  isLoading: boolean;
  loadBrandKit: (limitPerType?: number) => Promise<void>;
  loadMoreAssets: (type: BrandKitAssetType, currentOffset: number) => Promise<void>;
  addAsset: (type: BrandKitAssetType, asset: any) => void;
  removeAsset: (type: BrandKitAssetType, id: string) => Promise<void>;
  addColor: (input: SaveBrandKitColorInput) => Promise<void>;
  updateColor: (input: UpdateBrandKitColorInput) => Promise<void>;
  removeColor: (id: string) => Promise<void>;
  reorderColors: (orderedIds: string[]) => Promise<void>;
  addFontPreset: (input: SaveBrandKitFontPresetInput) => Promise<void>;
  updateFontPreset: (input: UpdateBrandKitFontPresetInput) => Promise<void>;
  removeFontPreset: (id: string) => Promise<void>;
  reorderFontPresets: (orderedIds: string[]) => Promise<void>;
}

// @ts-ignore
export const useBrandKitStore = create<BrandKitStore>((set: any, get: any) => ({
  logos: { ...emptySection },
  images: { ...emptySection },
  fonts: { ...emptySection },
  icons: { ...emptySection },
  colors: [],
  fontPresets: [],
  isLoading: false,

  loadBrandKit: async (limitPerType = 20) => {
    set({ isLoading: true });
    try {
      const res = await getBrandKit(limitPerType);
      const { data } = await toJSON(res);
      const kit = data.getBrandKit;
      const fontPresets = kit.fontPresets || [];
      set({
        logos: kit.logos,
        images: kit.images,
        fonts: kit.fonts,
        icons: kit.icons,
        colors: kit.colors,
        fontPresets,
        isLoading: false,
      });
      syncFirstPresetToLocalStorage(fontPresets);
    } catch (err: any) {
      console.error("Failed to load brand kit:", err);
      set({ isLoading: false });
    }
  },

  loadMoreAssets: async (type: BrandKitAssetType, currentOffset: number) => {
    try {
      const res = await getBrandKitAssets(type, 20, currentOffset);
      const { data } = await toJSON(res);
      const page = data.getBrandKitAssets as BrandKitSection;
      const key = SECTION_KEY[type];
      set((state: BrandKitStore) => ({
        ...state,
        [key]: {
          items: [...(state[key] as BrandKitSection).items, ...page.items],
          totalCount: page.totalCount,
          hasMore: page.hasMore,
        } as BrandKitSection,
      }));
    } catch (err: any) {
      console.error("Failed to load more assets:", err);
    }
  },

  addAsset: (type: BrandKitAssetType, asset: any) => {
    const key = SECTION_KEY[type];
    set((state: BrandKitStore) => ({
      ...state,
      [key]: {
        items: [...(state[key] as BrandKitSection).items, asset],
        totalCount: (state[key] as BrandKitSection).totalCount + 1,
        hasMore: (state[key] as BrandKitSection).hasMore,
      } as BrandKitSection,
    }));
  },

  removeAsset: async (type: BrandKitAssetType, id: string) => {
    try {
      const res = await removeBrandKitAssetMutation(id);
      const { data } = await toJSON(res);
      if (data.removeBrandKitAsset?.success) {
        const key = SECTION_KEY[type];
        set((state: BrandKitStore) => ({
          ...state,
          [key]: {
            items: (state[key] as BrandKitSection).items.filter((a: any) => a.id !== id),
            totalCount: (state[key] as BrandKitSection).totalCount - 1,
            hasMore: (state[key] as BrandKitSection).hasMore,
          } as BrandKitSection,
        }));
      } else {
        console.error("Failed to remove asset:", data.removeBrandKitAsset?.error);
      }
    } catch (err: any) {
      console.error("Failed to remove asset:", err);
    }
  },

  addColor: async (input: SaveBrandKitColorInput) => {
    try {
      const res = await saveBrandKitColor(input);
      const { data } = await toJSON(res);
      if (data.saveBrandKitColor?.success) {
        // Reload to get the new color with its server-assigned id
        await get().loadBrandKit();
      }
    } catch (err: any) {
      console.error("Failed to add color:", err);
    }
  },

  updateColor: async (input: UpdateBrandKitColorInput) => {
    try {
      const res = await updateBrandKitColorMutation(input);
      const { data } = await toJSON(res);
      if (data.updateBrandKitColor?.success) {
        await get().loadBrandKit();
      }
    } catch (err: any) {
      console.error("Failed to update color:", err);
    }
  },

  removeColor: async (id: string) => {
    try {
      await removeBrandKitColorMutation(id);
      set((state: BrandKitStore) => ({ colors: state.colors.filter((c: BrandKitColor) => c.id !== id) }));
    } catch (err: any) {
      console.error("Failed to remove color:", err);
    }
  },

  reorderColors: async (orderedIds: string[]) => {
    try {
      await reorderBrandKitColors(orderedIds);
      const current = get().colors;
      const reordered = orderedIds
        .map((id: string) => current.find((c: BrandKitColor) => c.id === id))
        .filter(Boolean) as BrandKitColor[];
      set({ colors: reordered });
    } catch (err: any) {
      console.error("Failed to reorder colors:", err);
    }
  },

  addFontPreset: async (input: SaveBrandKitFontPresetInput) => {
    try {
      const res = await saveBrandKitFontPresetMutation(input);
      const { data } = await toJSON(res);
      if (data.saveBrandKitFontPreset?.success) {
        await get().loadBrandKit();
      }
    } catch (err: any) {
      console.error("Failed to add font preset:", err);
    }
  },

  updateFontPreset: async (input: UpdateBrandKitFontPresetInput) => {
    try {
      const res = await updateBrandKitFontPresetMutation(input);
      const { data } = await toJSON(res);
      if (data.updateBrandKitFontPreset?.success) {
        await get().loadBrandKit();
      }
    } catch (err: any) {
      console.error("Failed to update font preset:", err);
    }
  },

  removeFontPreset: async (id: string) => {
    try {
      const res = await removeBrandKitFontPresetMutation(id);
      const { data } = await toJSON(res);
      if (data.removeBrandKitFontPreset?.success) {
        const updated = get().fontPresets.filter((p: BrandKitFontPreset) => p.id !== id);
        set({ fontPresets: updated });
        syncFirstPresetToLocalStorage(updated);
      }
    } catch (err: any) {
      console.error("Failed to remove font preset:", err);
    }
  },

  reorderFontPresets: async (orderedIds: string[]) => {
    try {
      await reorderBrandKitFontPresets(orderedIds);
      const current = get().fontPresets;
      const reordered = orderedIds
        .map((id: string) => current.find((p: BrandKitFontPreset) => p.id === id))
        .filter(Boolean) as BrandKitFontPreset[];
      set({ fontPresets: reordered });
      syncFirstPresetToLocalStorage(reordered);
    } catch (err: any) {
      console.error("Failed to reorder font presets:", err);
    }
  },
}));
